import React from 'react';
import {
  ChartBarIcon,
  CheckBadgeIcon,
  FlagIcon,
} from '@heroicons/react/20/solid';
import type { Achievement, AchievementClass } from '../../types/buildExpansion';

/**
 * One recognition badge. The three recognition classes are visually and
 * semantically distinct and must never be confused:
 *
 * - platform_achievement: computed by Bilt from documented build data. Always
 *   carries the "Bilt:Spec calculated" authority label plus full rank context.
 * - build_milestone: a documented moment in the build's own history. Labeled
 *   "Milestone", never ranked, never framed as a trophy.
 * - verified_credential: externally verified recognition, labeled as such.
 */

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const ordinal = (n: number): string => {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
};

const humanizeToken = (token: string): string => token.replace(/[_-]+/g, ' ').trim();

/** Human label for the scope an achievement was calculated within. */
export const formatScopeLabel = (scope: Record<string, unknown> | null): string | null => {
  if (!scope) return null;
  const preferredKeys = ['label', 'name', 'display', 'chassis', 'platform', 'model', 'make', 'category'];
  for (const key of preferredKeys) {
    const value = scope[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  const values = Object.values(scope)
    .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
    .map((v) => String(v).trim())
    .filter(Boolean);
  return values.length > 0 ? values.join(' · ') : null;
};

/**
 * Full rank context for a platform achievement, e.g.
 * "#3 of 184 · A90 Supra · by documented modifications".
 */
export const formatRankContext = (achievement: Achievement): string | null => {
  const parts: string[] = [];
  if (achievement.rank != null && achievement.population != null) {
    parts.push(`#${achievement.rank} of ${achievement.population}`);
  } else if (achievement.rank != null) {
    parts.push(`#${achievement.rank}`);
  } else if (achievement.percentile != null) {
    parts.push(`${ordinal(Math.round(achievement.percentile))} percentile`);
  }
  const scopeLabel = formatScopeLabel(achievement.scope);
  if (scopeLabel) parts.push(scopeLabel);
  if (achievement.metric) parts.push(`by ${humanizeToken(achievement.metric)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
};

interface ClassPresentation {
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  chipClass: string;
}

const CLASS_PRESENTATION: Record<AchievementClass, ClassPresentation> = {
  platform_achievement: {
    label: 'Bilt:Spec calculated',
    Icon: ChartBarIcon,
    chipClass: 'bg-orange-500/10 text-orange-300 border border-orange-500/25',
  },
  build_milestone: {
    label: 'Milestone',
    Icon: FlagIcon,
    chipClass: 'bg-[#111] text-gray-300 border border-white/[0.08]',
  },
  verified_credential: {
    label: 'Verified credential',
    Icon: CheckBadgeIcon,
    chipClass: 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/25',
  },
};

const TIER_CLASSES: Record<Achievement['visual_tier'], string> = {
  standard: 'border-white/[0.06] bg-[#111]/80',
  notable: 'border-white/10 bg-[#111] ring-1 ring-white/[0.04]',
  exceptional:
    'border-orange-500/35 bg-gradient-to-b from-orange-500/[0.1] to-[#111] ring-1 ring-orange-500/20',
};

export interface AchievementBadgeProps {
  achievement: Achievement;
  /** When provided the badge renders as a button (used for detail expansion). */
  onClick?: () => void;
  /** aria-expanded state when the badge controls an inline detail panel. */
  expanded?: boolean;
  className?: string;
}

export const AchievementBadge: React.FC<AchievementBadgeProps> = ({
  achievement,
  onClick,
  expanded,
  className = '',
}) => {
  const name = achievement.short_name || achievement.name;
  if (!name) return null;

  const presentation = achievement.achievement_class
    ? CLASS_PRESENTATION[achievement.achievement_class]
    : null;
  const isPlatform = achievement.achievement_class === 'platform_achievement';
  const isHistorical = achievement.status != null && achievement.status !== 'current';
  const rankContext = isPlatform ? formatRankContext(achievement) : null;
  const snapshotDate = isPlatform ? formatDate(achievement.issued_at || achievement.effective_at) : null;

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-white leading-snug">{name}</span>
        {isHistorical && (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-gray-400 border border-white/[0.08] rounded px-1.5 py-0.5">
            Historical
          </span>
        )}
      </div>

      {presentation && (
        <span
          className={`inline-flex items-center gap-1 self-start rounded-full px-2 py-0.5 text-[11px] font-medium ${presentation.chipClass}`}
        >
          <presentation.Icon className="w-3 h-3" aria-hidden />
          {presentation.label}
        </span>
      )}

      {rankContext && <p className="text-xs text-gray-400 leading-snug">{rankContext}</p>}

      {snapshotDate && (
        <p className="text-[11px] text-gray-500">
          Snapshot {snapshotDate}
        </p>
      )}
    </>
  );

  const tierClass = TIER_CLASSES[achievement.visual_tier] || TIER_CLASSES.standard;
  const frameClass = `flex flex-col gap-1.5 rounded-xl border px-3.5 py-3 ${tierClass} ${className}`;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-expanded={expanded}
        className={`${frameClass} w-full text-left cursor-pointer transition-colors duration-200 hover:border-white/[0.14] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400`}
      >
        {body}
      </button>
    );
  }

  return <div className={frameClass}>{body}</div>;
};

export default AchievementBadge;
